import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { formatMoney, fromMajor, money } from '@/lib/money'
import { cn } from '@/lib/cn'
import { DEFAULT_NIGHTS, HOTEL_CASHBACK, cityLabel, findStay } from './travel-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'
import { BRAND } from '@/brand/brand'

const EXTRAS = [
  { id: 'breakfast', title: 'Breakfast for two', body: 'Served in the courtyard', price: 0 },
  { id: 'transfer', title: 'Airport transfer', body: 'Both ways · private car', price: 48 },
  { id: 'late', title: 'Late check-out', body: 'Until 4 pm on the last day', price: 25 },
]

export function HotelBookingScreen() {
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const stay = findStay(id)
  const [chosen, setChosen] = useState<readonly string[]>(['breakfast'])
  const [split, setSplit] = useState(false)

  const model = useMemo(() => {
    if (!stay) return null
    const room = fromMajor(stay.nightly * DEFAULT_NIGHTS, 'USD')
    const extras = EXTRAS.filter((extra) => chosen.includes(extra.id)).reduce(
      (sum, extra) => sum + extra.price * 100,
      0,
    )
    const total = money(room.minorUnits + extras, 'USD')
    return {
      room,
      extras: money(extras, 'USD'),
      total,
      cashback: money(Math.round(total.minorUnits * HOTEL_CASHBACK), 'USD'),
      instalment: money(Math.round(total.minorUnits / 4), 'USD'),
    }
  }, [stay, chosen])

  if (!stay || !model) return <NotFoundScreen />

  const toggle = (extraId: string) =>
    setChosen((current) =>
      current.includes(extraId)
        ? current.filter((item) => item !== extraId)
        : [...current, extraId],
    )

  return (
    <>
      <PageHeader
        title={`Book ${stay.name}`}
        subtitle={`${stay.area}, ${cityLabel(stay.city)} · 12–15 Oct · 2 guests · free cancellation until 10 October`}
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to={`/travel/hotels/${stay.slug}`}>
            Back to the stay
          </Button>
        }
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Who is staying?
            </Text>
            <Stack gap={0} className="pb-mt-3">
              {[
                ['Guest', 'Alejandro Reyes Morales'],
                ['Email', 'alejandro@example.com'],
                ['Phone', '+1 (347) 555 0182'],
              ].map(([label, value]) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" tone="secondary">
                    {label}
                  </Text>
                  <Text variant="caption" weight="semibold">
                    {value}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Anything else?
            </Text>
            <Stack gap={2} className="pb-mt-3">
              {EXTRAS.map((extra) => (
                <button
                  key={extra.id}
                  type="button"
                  className={cn(styles.corridor, chosen.includes(extra.id) && styles.corridorActive)}
                  onClick={() => toggle(extra.id)}
                  aria-pressed={chosen.includes(extra.id)}
                >
                  <Icon name={chosen.includes(extra.id) ? 'check_circle' : 'add_circle'} size={20} />
                  <Stack grow gap={0} align="start">
                    <Text variant="caption" weight="semibold">
                      {extra.title}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {extra.body}
                    </Text>
                  </Stack>
                  <Text variant="caption" weight="semibold" numeric>
                    {extra.price === 0 ? 'Included' : `+$${extra.price}`}
                  </Text>
                </button>
              ))}
            </Stack>
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
                Total to pay
              </Text>
              <Pill tone="accent">3% back</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              {formatMoney(split ? model.instalment : model.total)}
            </Text>
            {split && (
              <Text variant="eyebrow" tone="onDarkMuted">
                today, then three more every two weeks
              </Text>
            )}
            <div className={checkout.rule} />
            {[
              [`Room · ${DEFAULT_NIGHTS} nights`, formatMoney(model.room)],
              ['Extras', formatMoney(model.extras)],
              ['Booking fee', 'None'],
              ['Cashback after check-out', `+${formatMoney(model.cashback)}`],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  tone={index === 3 ? 'accent' : 'onDark'}
                  numeric
                >
                  {value}
                </Text>
              </div>
            ))}
            <Card.Footer>
              <Button
                size="lg"
                block
                iconStart="lock"
                onClick={() => navigate(`/travel/hotels/${stay.slug}/done`, { replace: true })}
              >
                Pay with your {BRAND.name} card
              </Button>
              <Button
                variant="onDark"
                size="md"
                block
                className="pb-mt-3"
                onClick={() => setSplit((value) => !value)}
              >
                {split ? 'Pay it all today' : 'Split into 4 · 0% interest'}
              </Button>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
